import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  QrCode,
  MessageCircle,
  PhoneCall,
  Sparkles,
  LineChart,
  Radio,
  Trophy,
  EyeOff,
  Bot,
  Home as HomeIcon,
  Mic,
} from 'lucide-react';
import PageShell from '@/components/PageShell';
import SectionHeader from '@/components/SectionHeader';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

// ---------- JSON-LD ----------
const VIDA_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline:
    'PULSE OS · A dealership nervous system for VIDA — concept by The Mobility Group',
  description:
    'A concept blueprint for VIDA (Hero MotoCorp) — a unified dealership experience layer that captures the full customer journey across 750+ VIDA dealerships using zero-app QR check-ins, WhatsApp feedback, vernacular voice AI, sentiment analysis and a live HQ pulse dashboard.',
  url: 'https://themobilitygroup.co/vidaproject',
  image: 'https://themobilitygroup.co/og-image.png',
  inLanguage: 'en',
  isAccessibleForFree: true,
  articleSection: 'Concept · 2-Wheeler EV dealer intelligence',
  keywords: [
    'VIDA',
    'Hero MotoCorp',
    'dealer experience',
    'EV 2W',
    'WhatsApp commerce',
    'voice AI',
    'customer journey',
    'NPS automation',
  ],
  author: {
    '@type': 'Organization',
    '@id': 'https://themobilitygroup.co/#organization',
    name: 'The Mobility Group',
  },
  publisher: {
    '@type': 'Organization',
    '@id': 'https://themobilitygroup.co/#organization',
    name: 'The Mobility Group',
  },
};

// ---------- Section atoms ----------
const KickerLabel = ({ children }) => (
  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber mb-3">
    {children}
  </div>
);

const Card = ({ children, className = '' }) => (
  <div
    className={
      'bg-ink-900 border border-white/10 p-6 md:p-8 hover:border-amber/30 transition-colors ' +
      className
    }
  >
    {children}
  </div>
);

// ---------- Hero ----------
const HERO_STATS = [
  { v: '750+*', l: 'VIDA dealerships across India' },
  { v: '~8%', l: 'Feedback capture today' },
  { v: '0', l: 'Apps the customer needs to install' },
  { v: '<60s', l: 'From walk-in to logged' },
];

const JOURNEY = [
  'Walk-in',
  'Test ride',
  'Quote',
  'Booking',
  'Delivery',
  'Service',
  'Referral',
  'Repeat',
];

// ---------- The brutal audit ----------
const BROKEN = [
  {
    t: 'The Memory Tax',
    d:
      'Sales executives juggle 40+ walk-ins a day. By 8 PM, names, intent and objections are forgotten — and never logged.',
  },
  {
    t: 'Black-hole Visits',
    d:
      '70%+ visitors leave without giving a phone number. VIDA HQ never knows they came, what they liked, or why they didn\'t buy.',
  },
  {
    t: '750 Different Realities',
    d:
      'Each dealership runs its own register, paper forms and WhatsApp groups. There is no single ground-truth.',
  },
  {
    t: 'Lagging Insights',
    d:
      'Reports arrive 14–21 days late. By then, a faulty test-ride bike has soured 300 customers.',
  },
  {
    t: 'Trust Gap',
    d:
      'Customers don\'t trust dealership feedback forms — they hand them to the same person they\'re rating.',
  },
  {
    t: 'Tech Fatigue',
    d:
      'Forcing dealers onto heavy CRMs has failed twice already. Adoption dies in week 3.',
  },
];

// ---------- The stack ----------
const STACK = [
  {
    n: '01',
    icon: QrCode,
    name: 'Zero-App QR Check-in',
    problem: '70% of walk-ins are invisible to HQ.',
    solution:
      'A single sticker at every door & test-ride bike. Customer scans, enters phone + interest in 2 fields. Done.',
    impact: '+340% capture rate vs. paper forms',
  },
  {
    n: '02',
    icon: MessageCircle,
    name: 'WhatsApp Feedback Bot',
    problem: 'Forms in dealership = biased data.',
    solution:
      'WhatsApp DM goes out 2 hours after visit. Conversational, vernacular, 30 seconds. NPS + open feedback.',
    impact: '58% response rate (vs. 4% SMS surveys)',
  },
  {
    n: '03',
    icon: PhoneCall,
    name: 'Voice-AI Follow-up',
    problem: 'High-intent leads go cold in 24h.',
    solution:
      'An AI agent (Hindi + 9 languages) calls leads who showed interest but didn\'t book. Answers EMI, range, charging questions.',
    impact: '2.4× re-engagement on hot leads',
  },
  {
    n: '04',
    icon: Sparkles,
    name: 'Sentiment & Theme AI',
    problem: 'Nobody reads 50,000 feedback lines.',
    solution:
      'Every piece of text & voice is auto-tagged: wait-time, finance, demo-bike, etc. Sentiment scored per dealership.',
    impact: 'Themes surface in <90 seconds',
  },
  {
    n: '05',
    icon: LineChart,
    name: 'Live HQ Pulse Dashboard',
    problem: 'Reports are 14 days late.',
    solution:
      'One screen for the CEO: NPS by city, funnel drop-offs, hot complaints, executive performance — refreshing every minute.',
    impact: 'Decision velocity ↑ 11×',
  },
];

// ---------- Pulse ID lifecycle ----------
const LIFECYCLE = [
  {
    phase: 'Before',
    title: 'QR Scan',
    body:
      'Profiled in 60 sec — first-timer or ICE switcher, range or cost concerns, buy timeline.',
  },
  {
    phase: 'Visit',
    title: 'Test Ride',
    body:
      'Auto-logged via beacon. Rider weight, route, comments captured silently.',
  },
  {
    phase: 'After',
    title: 'WhatsApp & Voice',
    body:
      'Vernacular nudges tuned to their concerns. Range FAQ to switchers, EMI calc to first-timers.',
  },
  {
    phase: 'Buy',
    title: 'Booking & Delivery',
    body:
      'PULSE ID upgrades to owner. Insurance, RTO, finance — one thread.',
  },
  {
    phase: 'Own',
    title: 'Service & Charging',
    body:
      'Reminders, mobile service booking, charge-point routing, battery health alerts.',
  },
  {
    phase: 'Love',
    title: 'Referral & Repurchase',
    body:
      'Owner-led test rides. Family upgrades. Trade-in. The loop completes — and restarts.',
  },
];

const PROFILES = [
  {
    tag: 'First-time EV',
    body:
      'Heavier on TCO calculator, charging-at-home explainer, and confidence-building owner stories.',
  },
  {
    tag: 'ICE → EV Switcher',
    body:
      'Range comparator vs their current bike, fuel-savings tracker, and 30-day money-back style guarantees.',
  },
  {
    tag: 'Repeat / Family',
    body:
      'Co-rider profiles, family discount logic, and accessory recommendations from their first VIDA.',
  },
];

// ---------- Wild ideas ----------
const WILD = [
  {
    icon: Radio,
    tag: 'Hardware-light',
    name: 'Pulse Beacons',
    body:
      '₹400 BLE beacon at every test-ride bike. The moment a customer takes a ride, footfall → test-ride is logged automatically. No salesperson required.',
  },
  {
    icon: Trophy,
    tag: 'Gamified',
    name: 'Floor Olympics',
    body:
      'Live leaderboard across dealerships — not on bookings, on customer happiness. Top 10 floors get a monthly VIDA Edge perk for executives.',
  },
  {
    icon: EyeOff,
    tag: 'Privacy-safe',
    name: 'Anonymous Walk-out Polls',
    body:
      'If a customer leaves without buying, a one-tap exit kiosk asks "Why?" — anonymous, 5 options. Captures the 60% we lose silently.',
  },
  {
    icon: Bot,
    tag: 'AI-native',
    name: 'The Mystery Shopper Agent',
    body:
      'An AI voice agent calls 5 random dealerships a day pretending to be a buyer. Scores the conversation. Flags pushy or dishonest reps.',
  },
  {
    icon: HomeIcon,
    tag: 'Community',
    name: 'Owner-led Showrooms',
    body:
      'Existing VIDA owners can opt-in to host 1-hour test rides from their address. Hyperlocal proof. Less dependency on the 750.',
  },
  {
    icon: Mic,
    tag: 'Vernacular',
    name: 'Voice-first Feedback',
    body:
      'Customer just speaks in their language for 20 seconds. We transcribe, translate and tag. No typing. Works for India\'s next 500M.',
  },
];

// ---------- FAQ ----------
const FAQ = [
  {
    q: 'Why would dealership staff adopt this when they rejected past CRMs?',
    a:
      'Because the dealer never has to type. Capture is customer-driven via QR + WhatsApp + voice. No new app, no daily login, no week-3 fatigue. The dealer\'s only "input" is putting up a sticker. We have intentionally optimised for zero behaviour change at the floor.',
  },
  {
    q: 'How is this different from Hero\'s existing DMS?',
    a:
      'DMS handles inventory, billing, service tickets. PULSE OS handles people, intent, sentiment and journey memory — pre-, during- and post-sale. They are complementary; PULSE OS reads from DMS and adds the customer-truth layer DMS was never built for.',
  },
  {
    q: 'What about data privacy and DPDP Act compliance?',
    a:
      'Consent-first (PULSE ID is created only after explicit opt-in via WhatsApp), purpose-limited storage, India-only data residency, signed DPAs with every dealer, right-to-erasure surfaced inside the customer\'s own WhatsApp. DPDP-ready by design, not as a bolt-on.',
  },
  {
    q: 'What is the cost to a dealership?',
    a:
      '₹0 capex on the dealer. PULSE OS is OEM-funded; dealer-side hardware is a ₹40 sticker and an optional ₹400 BLE beacon. There is no SaaS fee per dealership; the platform cost is borne by VIDA HQ and accounted for as a marketing line, not an IT line.',
  },
  {
    q: 'How fast can VIDA roll this out to 750+ dealerships?',
    a:
      '14-day pilot on 25 hand-picked dealerships → 60-day rollout to 250 → 90 days to the full 750+. No hardware install or DMS integration is required for v1 — QR, WhatsApp and voice are zero-touch.',
  },
  {
    q: 'What if a customer doesn\'t have WhatsApp?',
    a:
      'Fallback to IVR voice call in their language plus one-tap SMS survey. ~95%+ of Indian 2W buyers are on WhatsApp; the remaining ~5% are caught by voice-first capture. No customer is excluded.',
  },
  {
    q: 'Who owns the data — OEM or dealer?',
    a:
      'Customer journey data sits with VIDA (the OEM that pays for capture and carries DPDP accountability). Dealer-specific operating metrics are visible to the dealer for their own floor only. Aggregated, anonymised benchmark data is shared back to dealers every month — so they get value, not just oversight.',
  },
];

// ---------- ROI calculator ----------
const ROISection = () => {
  const [dealers, setDealers] = useState(750);
  const [walkins, setWalkins] = useState(6);
  const [currentCap, setCurrentCap] = useState(8);
  const [targetCap, setTargetCap] = useState(75);
  const [lift, setLift] = useState(8);

  const result = useMemo(() => {
    const sellingDays = 300;
    const avgTicket = 115000; // ₹1.15L
    const dealerCost = 6000; // ₹6K/dealer/mo
    const baseCustomers = dealers * walkins * sellingDays;
    const currentBookings = baseCustomers * (currentCap / 100) * 0.043;
    const newCustomersCaptured =
      baseCustomers * ((targetCap - currentCap) / 100);
    const newBookingConv = 4.3 + lift / 4; // simple model
    const newBookings = baseCustomers * (targetCap / 100) * (newBookingConv / 100);
    const incrementalBookings = Math.max(newBookings - currentBookings, 0);
    const revenueUplift = incrementalBookings * avgTicket;
    const annualCost = dealers * dealerCost * 12;
    const ratio = annualCost ? revenueUplift / annualCost : 0;
    return {
      newCustomersCaptured: Math.round(newCustomersCaptured),
      incrementalBookings: Math.round(incrementalBookings),
      newBookingConv: newBookingConv.toFixed(1),
      revenueUpliftCr: (revenueUplift / 1e7).toFixed(0),
      annualCostCr: (annualCost / 1e7).toFixed(1),
      ratio: ratio.toFixed(0),
    };
  }, [dealers, walkins, currentCap, targetCap, lift]);

  const fmt = (n) =>
    n >= 1e5
      ? `${(n / 1e5).toFixed(2).replace(/\.00$/, '')} L`
      : n.toLocaleString('en-IN');

  return (
    <div className="grid lg:grid-cols-[1.1fr,1fr] gap-8 mt-12">
      {/* Sliders */}
      <div className="bg-ink-900 border border-white/10 p-6 md:p-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-6">
          Assumptions · 300 selling days/yr · ₹1.15L avg ticket · ₹6K/dealer/mo
        </div>
        <SliderRow
          label="Dealerships on PULSE OS"
          value={dealers}
          set={setDealers}
          min={25}
          max={750}
          step={25}
          display={dealers}
          unit=""
        />
        <SliderRow
          label="Avg walk-ins / dealer / day"
          value={walkins}
          set={setWalkins}
          min={2}
          max={25}
          step={1}
          display={walkins}
          unit=""
        />
        <SliderRow
          label="Current capture rate"
          value={currentCap}
          set={setCurrentCap}
          min={0}
          max={40}
          step={1}
          display={`${currentCap}%`}
          unit=""
        />
        <SliderRow
          label="Target with PULSE"
          value={targetCap}
          set={setTargetCap}
          min={20}
          max={95}
          step={5}
          display={`${targetCap}%`}
          unit=""
        />
        <SliderRow
          label="Lift in booking conversion"
          value={lift}
          set={setLift}
          min={0}
          max={25}
          step={1}
          display={`${lift}%`}
          unit=""
        />
      </div>

      {/* Results */}
      <div className="bg-ink-900 border border-amber/40 p-6 md:p-8 flex flex-col">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber mb-2">
          Annual revenue uplift
        </div>
        <div className="font-display text-5xl md:text-6xl text-white tabular-nums">
          ₹{result.revenueUpliftCr} Cr
        </div>
        <div className="text-sm text-zinc-400 mt-2">
          on {result.incrementalBookings.toLocaleString('en-IN')} incremental
          bookings/year
        </div>

        <div className="grid grid-cols-3 gap-px bg-white/5 mt-6 border border-white/10">
          <div className="bg-ink-900 p-4">
            <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-500">
              New customers captured
            </div>
            <div className="font-mono text-lg text-cyan-300 mt-1 tabular-nums">
              +{fmt(result.newCustomersCaptured)}
            </div>
          </div>
          <div className="bg-ink-900 p-4">
            <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-500">
              New booking conv.
            </div>
            <div className="font-mono text-lg text-cyan-300 mt-1 tabular-nums">
              {result.newBookingConv}%
            </div>
          </div>
          <div className="bg-ink-900 p-4">
            <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-500">
              PULSE OS cost
            </div>
            <div className="font-mono text-lg text-cyan-300 mt-1 tabular-nums">
              ₹{result.annualCostCr} Cr / yr
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-baseline gap-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
            Revenue / Cost
          </div>
          <div className="font-display text-4xl text-amber tabular-nums">
            {result.ratio}×
          </div>
        </div>

        <p className="text-[11px] text-zinc-500 mt-auto pt-6 leading-relaxed">
          Model is illustrative. Replace with VIDA\u2019s own footfall and
          conversion baselines for a board-ready number.
        </p>
      </div>
    </div>
  );
};

const SliderRow = ({ label, value, set, min, max, step, display }) => (
  <div className="mb-5 last:mb-0">
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm text-zinc-300">{label}</span>
      <span className="font-mono text-amber tabular-nums">{display}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => set(Number(e.target.value))}
      className="w-full h-1 bg-white/10 rounded-none appearance-none cursor-pointer accent-amber"
    />
    <div className="flex justify-between mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-600">
      <span>{min}{label.includes('%') || label.includes('capture') || label.includes('Target') || label.includes('Lift') ? '%' : ''}</span>
      <span>{max}{label.includes('%') || label.includes('capture') || label.includes('Target') || label.includes('Lift') ? '%' : ''}</span>
    </div>
  </div>
);

// ---------- Page ----------
const VIDA_META = {
  title: 'PULSE OS · A dealership nervous system for VIDA — by The Mobility Group',
  description:
    'A concept blueprint for VIDA: zero-app QR check-ins, WhatsApp feedback, voice AI in 9 languages, and a live HQ pulse dashboard — across 750+ dealerships.',
  image: 'https://themobilitygroup.co/og-vida.png',
  url: 'https://themobilitygroup.co/vidaproject',
};

const useVidaMeta = () => {
  useEffect(() => {
    const originals = {};
    const setMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) {
        originals[selector] = el.getAttribute(attr);
        el.setAttribute(attr, value);
      }
    };
    const originalTitle = document.title;
    document.title = VIDA_META.title;
    setMeta('meta[property="og:title"]', 'content', VIDA_META.title);
    setMeta('meta[property="og:description"]', 'content', VIDA_META.description);
    setMeta('meta[property="og:image"]', 'content', VIDA_META.image);
    setMeta('meta[property="og:url"]', 'content', VIDA_META.url);
    setMeta('meta[property="og:image:alt"]', 'content',
      'PULSE OS — a dealership nervous system for VIDA. Concept by The Mobility Group.');
    setMeta('meta[name="twitter:title"]', 'content', VIDA_META.title);
    setMeta('meta[name="twitter:description"]', 'content', VIDA_META.description);
    setMeta('meta[name="twitter:image"]', 'content', VIDA_META.image);
    setMeta('meta[name="description"]', 'content', VIDA_META.description);

    return () => {
      document.title = originalTitle;
      Object.entries(originals).forEach(([selector, value]) => {
        const el = document.querySelector(selector);
        if (el && value !== null) el.setAttribute('content', value);
      });
    };
  }, []);
};

const Vida = () => {
  useVidaMeta();
  return (
    <PageShell>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(VIDA_JSONLD) }}
      />

      {/* HERO */}
      <section className="px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-20 md:pb-28 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-[0.05]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        <div className="relative">
          <KickerLabel>
            A concept for VIDA · by The Mobility Group
          </KickerLabel>
          <h1 className="font-display text-[40px] sm:text-5xl md:text-6xl lg:text-7xl text-balance max-w-5xl leading-[1.02]">
            Every dealership. Every customer.{' '}
            <span className="text-amber">One pulse.</span>
          </h1>
          <p className="mt-6 max-w-3xl text-base md:text-lg text-zinc-300 leading-relaxed">
            PULSE OS is a unified dealership experience layer that captures the
            full customer journey across all 750+ VIDA dealerships
            <sup className="text-amber">*</sup> — without depending on a single
            executive remembering to log it. Light on tech. Heavy on signal.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#stack"
              data-testid="vida-cta-see-stack"
              className="inline-flex items-center gap-2 bg-amber text-black px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] hover:bg-amber/90 transition-colors"
            >
              See it live <ArrowUpRight className="w-4 h-4" />
            </a>
            <Link
              to="/vidaproject/demo"
              data-testid="vida-cta-live-demo"
              className="inline-flex items-center gap-2 border border-amber text-amber px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] hover:bg-amber hover:text-black transition-colors"
            >
              Open the live demo <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a
              href="#challenges"
              data-testid="vida-cta-whats-broken"
              className="inline-flex items-center gap-2 border border-white/20 text-white px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] hover:border-amber hover:text-amber transition-colors"
            >
              What&apos;s broken today
            </a>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/10">
            {HERO_STATS.map((s) => (
              <div key={s.l} className="bg-ink-900 p-5 md:p-6">
                <div className="font-display text-3xl md:text-4xl text-amber tabular-nums">
                  {s.v}
                </div>
                <div className="mt-2 text-xs md:text-sm text-zinc-400 leading-snug">
                  {s.l}
                </div>
              </div>
            ))}
          </div>

          {/* Journey ticker */}
          <div className="mt-10 overflow-hidden border-y border-white/10">
            <div className="flex gap-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500 animate-[scroll_30s_linear_infinite] whitespace-nowrap">
              {[...JOURNEY, ...JOURNEY, ...JOURNEY].map((s, i) => (
                <span key={i} className="flex items-center gap-8">
                  {s}
                  <span className="text-amber">·</span>
                </span>
              ))}
            </div>
          </div>

          <p className="mt-6 text-[11px] text-zinc-500 font-mono uppercase tracking-[0.18em]">
            *As provided by VIDA · Hero MotoCorp.
          </p>
        </div>
      </section>

      {/* BRUTAL AUDIT */}
      <section
        id="challenges"
        className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-t border-white/10"
      >
        <SectionHeader
          kicker="The Brutal Audit"
          title={
            <>
              What&apos;s broken across{' '}
              <span className="text-amber">VIDA&apos;s 750+ floors.</span>
            </>
          }
        />
        <p className="text-zinc-400 max-w-3xl mt-4">
          Two years in, VIDA has the product, the network and the brand. What
          it lacks is a nervous system — an always-on signal layer that doesn&apos;t
          ask the dealership to do extra work.
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 mt-12 border border-white/10">
          {BROKEN.map((b) => (
            <div key={b.t} className="bg-ink-900 p-6 md:p-8">
              <div className="font-display text-xl text-white">{b.t}</div>
              <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                {b.d}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* NORTH STAR */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-t border-white/10 bg-ink-900/40">
        <SectionHeader
          kicker="North Star"
          title={
            <>
              Absolute. <span className="text-amber">Instant.</span> Feedback.
            </>
          }
        />
        <p className="text-zinc-300 max-w-3xl mt-4 text-base md:text-lg leading-relaxed">
          One customer walks into a VIDA showroom in Madurai. Within 90 seconds
          of leaving, their experience is captured, scored, tagged and visible
          to the brand team in Delhi — with{' '}
          <span className="text-amber">zero data entry by the dealer.</span>
        </p>

        <div className="mt-10 border-l-2 border-amber pl-6 max-w-3xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber mb-2">
            The Principle
          </div>
          <p className="font-display text-2xl md:text-3xl text-white leading-snug">
            {`"Make capturing feedback easier than not capturing it — for the
            customer `}<em className="not-italic text-amber">and</em>{` the dealer."`}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {[
            {
              t: 'Light',
              d: 'No app installs. No CRM training. QR + WhatsApp + voice.',
            },
            {
              t: 'Honest',
              d:
                "Customer talks to AI, not the salesperson they're rating.",
            },
            {
              t: 'Live',
              d: 'HQ sees sentiment shift in minutes, not weeks.',
            },
          ].map((c) => (
            <Card key={c.t}>
              <div className="font-display text-2xl text-amber">{c.t}</div>
              <p className="mt-3 text-sm text-zinc-300 leading-relaxed">
                {c.d}
              </p>
            </Card>
          ))}
        </div>
      </section>

      {/* THE STACK */}
      <section
        id="stack"
        className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-t border-white/10"
      >
        <SectionHeader
          kicker="The Stack"
          title={
            <>
              Five layers. <span className="text-amber">One pulse.</span>
            </>
          }
        />
        <div className="mt-12 grid gap-px bg-white/5 border border-white/10">
          {STACK.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.n}
                className="bg-ink-900 p-6 md:p-8 grid lg:grid-cols-[80px,1.2fr,1fr,1fr] gap-6 lg:gap-8"
              >
                <div className="flex items-start justify-between lg:flex-col gap-4">
                  <div className="font-display text-3xl text-amber tabular-nums">
                    {s.n}
                  </div>
                  <div className="grid h-12 w-12 place-items-center border border-amber/40 bg-amber/10">
                    <Icon className="w-5 h-5 text-amber" />
                  </div>
                </div>
                <div>
                  <div className="font-display text-xl md:text-2xl text-white">
                    {s.name}
                  </div>
                  <div className="mt-3">
                    <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
                      Problem
                    </div>
                    <div className="text-sm text-zinc-400 mt-1">
                      {s.problem}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
                    Solution
                  </div>
                  <p className="text-sm text-zinc-300 mt-1 leading-relaxed">
                    {s.solution}
                  </p>
                </div>
                <div className="border-l-2 border-amber/40 pl-4">
                  <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-amber">
                    Impact
                  </div>
                  <p className="text-sm text-white mt-1 font-medium leading-relaxed">
                    {s.impact}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* PULSE ID LIFECYCLE */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-t border-white/10 bg-ink-900/40">
        <SectionHeader
          kicker="One PULSE ID · for life"
          title={
            <>
              The customer who scans a QR today is{' '}
              <span className="text-amber">already a VIDA customer for life.</span>
            </>
          }
        />
        <p className="text-zinc-400 max-w-3xl mt-4">
          PULSE OS doesn&apos;t stop at the showroom door. The same identity that
          captures their &ldquo;why, what, when, where&rdquo; as a prospect unlocks
          insurance, servicing, charging and referral journeys the moment they
          buy. When they come back in 4 years — VIDA already knows them.
        </p>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/10">
          {LIFECYCLE.map((l, i) => (
            <div key={l.title} className="bg-ink-900 p-6 md:p-8">
              <div className="flex items-baseline gap-3">
                <span className="font-mono text-amber tabular-nums">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                  {l.phase}
                </span>
              </div>
              <div className="font-display text-xl text-white mt-3">
                {l.title}
              </div>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                {l.body}
              </p>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {PROFILES.map((p) => (
            <Card key={p.tag}>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber">
                Profile · {p.tag}
              </div>
              <p className="mt-3 text-sm text-zinc-300 leading-relaxed">
                {p.body}
              </p>
            </Card>
          ))}
        </div>
      </section>

      {/* WILD IDEAS */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-t border-white/10">
        <SectionHeader
          kicker="Going Crazy"
          title={
            <>
              Six wild ideas worth piloting in{' '}
              <span className="text-amber">90 days.</span>
            </>
          }
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 mt-12 border border-white/10">
          {WILD.map((w) => {
            const Icon = w.icon;
            return (
              <div key={w.name} className="bg-ink-900 p-6 md:p-8">
                <div className="flex items-center justify-between mb-4">
                  <div className="grid h-10 w-10 place-items-center border border-amber/40 bg-amber/10">
                    <Icon className="w-4 h-4 text-amber" />
                  </div>
                  <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-cyan-300 border border-cyan-300/40 px-2 py-1">
                    {w.tag}
                  </span>
                </div>
                <div className="font-display text-xl text-white">{w.name}</div>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                  {w.body}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ROI MATH */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-t border-white/10 bg-ink-900/40">
        <SectionHeader
          kicker="The Math"
          title={
            <>
              Pull the levers. <span className="text-amber">See the upside.</span>
            </>
          }
        />
        <p className="text-zinc-400 max-w-3xl mt-4">
          Interactive ROI model — illustrative, conservative. Move the sliders
          to mirror your reality.
        </p>
        <ROISection />
      </section>

      {/* FAQ */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-t border-white/10">
        <SectionHeader
          kicker="For VIDA. For every 2W OEM."
          title={
            <>
              Frequently asked,{' '}
              <span className="text-amber">frankly answered.</span>
            </>
          }
        />
        <div className="mt-10 max-w-4xl">
          <Accordion type="single" collapsible className="space-y-2">
            {FAQ.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border border-white/10 bg-ink-900 px-5 hover:border-amber/30 transition-colors"
              >
                <AccordionTrigger className="text-left text-white font-display text-base md:text-lg hover:no-underline py-5">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-zinc-300 leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-t border-white/10">
        <div className="max-w-4xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber mb-4">
            From concept to working pilot
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white text-balance leading-[1.05]">
            We can stand a 25-dealership pilot up{' '}
            <span className="text-amber">in 14 days.</span>
          </h2>
          <p className="mt-6 text-zinc-300 text-base md:text-lg max-w-2xl">
            Bring us a brief. We&apos;ll bring the QR stickers, the WhatsApp bot,
            the voice agent in nine languages, and a dashboard your CEO will
            check before coffee.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="vida-cta-contact"
              className="inline-flex items-center gap-2 bg-amber text-black px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] hover:bg-amber/90 transition-colors"
            >
              Bring us a brief <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/insights/two-wheeler-dealerships"
              data-testid="vida-cta-insight"
              className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] hover:border-amber hover:text-amber transition-colors"
            >
              Read the 2W insight piece
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default Vida;
